import { CalendarPlus, CalendarSync } from 'lucide-react';

import { defineTour, type TourResolver } from '@nucleus-commerce/admin-kit/tour';
import { getAdminV1OrgSchedules } from '@nucleus-commerce/api-client/admin';

/** An active schedule shows the most of the page (next run, live counts); else the newest one. */
const scheduleForDetail: TourResolver = {
  key: 'schedule-for-detail',
  thing: 'things.schedule',
  load: async () => {
    const active = await getAdminV1OrgSchedules({ limit: 1, status: 'active' });
    const schedule = active.items[0] ?? (await getAdminV1OrgSchedules({ limit: 1 })).items[0];
    return schedule ? { scheduleId: schedule.id } : null;
  },
};

export const schedulesListTour = defineTour({
  id: 'schedules.list',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/schedules/',
  scope: 'org',
  requires: 'products.read',
  icon: CalendarSync,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'schedules.list.table' },
        { target: 'schedules.list.empty', copy: 'empty' },
      ],
    },
    { id: 'filters', kind: 'explain', target: 'schedules.list.filters', optional: true },
    { id: 'upcoming', kind: 'explain', target: 'schedules.list.upcoming' },
    { id: 'new', kind: 'action', target: 'schedules.list.new', requires: 'products.write' },
  ],
  related: ['schedules.new', 'schedules.detail'],
});

export const scheduleNewTour = defineTour({
  id: 'schedules.new',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/schedules/new',
  scope: 'org',
  requires: 'products.write',
  icon: CalendarPlus,
  steps: [
    { id: 'stores', kind: 'explain', target: 'schedules.form.stores', placement: 'right' },
    { id: 'cadence', kind: 'explain', target: 'schedules.form.cadence', placement: 'right' },
    { id: 'pool', kind: 'explain', target: 'schedules.form.pool', placement: 'right' },
    { id: 'rules', kind: 'explain', target: 'schedules.form.rules', placement: 'right' },
    { id: 'ending', kind: 'explain', target: 'schedules.form.ending', placement: 'left' },
    { id: 'save', kind: 'explain', target: 'schedules.form.save', placement: 'left' },
  ],
  related: ['schedules.list', 'schedules.detail'],
});

export const scheduleDetailTour = defineTour({
  id: 'schedules.detail',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/schedules/$scheduleId',
  scope: 'record',
  resolve: scheduleForDetail,
  requires: 'products.read',
  icon: CalendarSync,
  steps: [
    { id: 'actions', kind: 'explain', target: 'schedules.detail.actions', placement: 'left' },
    { id: 'summary', kind: 'explain', target: 'schedules.detail.summary' },
    { id: 'stores', kind: 'explain', target: 'schedules.detail.stores' },
    { id: 'pool', kind: 'explain', target: 'schedules.detail.pool-tab' },
    { id: 'runs', kind: 'explain', target: 'schedules.detail.runs-tab' },
    { id: 'upcoming', kind: 'explain', target: 'schedules.detail.upcoming-tab' },
    { id: 'settings', kind: 'explain', target: 'schedules.detail.settings-tab' },
  ],
  related: ['schedules.list', 'store-catalog.list'],
});
